// The app's translator. Every component takes `useTranslations` from here,
// never from use-intl directly (lint-enforced): it is use-intl's translator
// with one addition — interpolated strings are bidi-isolated.
//
// A name or title the user typed can be in any script. Dropped raw into a
// sentence, the Unicode bidi algorithm lets an Arabic or Hebrew value pull
// its neighbours (spaces, digits, punctuation, emoji) into its own run:
// "{name} · 3 members" renders as "3 · <name> members". Wrapping each value
// in U+2068 FIRST STRONG ISOLATE … U+2069 POP DIRECTIONAL ISOLATE lays it
// out as a sealed unit, in either direction. This is Fluent's `useIsolating`
// default and the W3C i18n recommendation for inserted text.
//
// Only plain `{arg}` substitutions are isolated. A value that feeds an ICU
// `select` / `plural` / `selectordinal` / `number` / `date` / `time` must
// reach the formatter untouched — wrapped, `{kind, select, circle {…}}`
// would stop matching `circle`. Numbers, dates and rich-text tag functions
// are never strings, so they pass through as-is.
import { useMemo, type ReactNode } from 'react';
import { useTranslations as useIntlTranslations } from 'use-intl';
import type {
  Formats,
  MarkupTranslationValues,
  RichTranslationValues,
  TranslationValues,
} from 'use-intl';

const FIRST_STRONG_ISOLATE = '⁨';
const POP_DIRECTIONAL_ISOLATE = '⁩';

const TYPED_ARGUMENT = /\{\s*([A-Za-z0-9_]+)\s*,\s*(select|plural|selectordinal|number|date|time)\b/g;

// Message sources are a fixed set per bundle, so this stays bounded.
const typedArgumentsBySource = new Map<string, ReadonlySet<string>>();

function typedArguments(source: string): ReadonlySet<string> {
  const cached = typedArgumentsBySource.get(source);
  if (cached !== undefined) return cached;
  const names = new Set<string>();
  for (const match of source.matchAll(TYPED_ARGUMENT)) {
    const name = match[1];
    if (name !== undefined) names.add(name);
  }
  typedArgumentsBySource.set(source, names);
  return names;
}

/** Seals one user-typed string (a name, a title) as its own bidi run. */
export function isolateBidi(value: string): string {
  return `${FIRST_STRONG_ISOLATE}${value}${POP_DIRECTIONAL_ISOLATE}`;
}

function isolateValues<V extends RichTranslationValues | MarkupTranslationValues>(
  values: V,
  typed: ReadonlySet<string>,
): V {
  const isolated = { ...values };
  for (const [name, value] of Object.entries(values)) {
    if (typeof value === 'string' && !typed.has(name)) {
      isolated[name as keyof V] = isolateBidi(value) as V[keyof V];
    }
  }
  return isolated;
}

type BaseTranslator = {
  (key: string, values?: TranslationValues, formats?: Formats): string;
  rich(key: string, values?: RichTranslationValues, formats?: Formats): ReactNode;
  markup(key: string, values: MarkupTranslationValues, formats?: Formats): string;
  raw(key: string): unknown;
  has(key: string): boolean;
};

function isolating<T>(translator: T): T {
  // Library boundary (Anti-patterns: `unknown` only at validated boundaries):
  // use-intl's translator is a set of per-key generic overloads typed from
  // the message catalog. At runtime they are this one shape, so the wrapper
  // is written against it and hands back the caller's exact type `T`.
  const t = translator as unknown as BaseTranslator;
  const isolate = <V extends RichTranslationValues | MarkupTranslationValues>(
    key: string,
    values: V | undefined,
  ): V | undefined => {
    if (values === undefined || !t.has(key)) return values;
    const source = t.raw(key);
    return isolateValues(values, typeof source === 'string' ? typedArguments(source) : new Set());
  };

  const wrapped = (key: string, values?: TranslationValues, formats?: Formats) =>
    t(key, isolate(key, values), formats);
  wrapped.rich = (key: string, values?: RichTranslationValues, formats?: Formats) =>
    t.rich(key, isolate(key, values), formats);
  wrapped.markup = (key: string, values: MarkupTranslationValues, formats?: Formats) =>
    t.markup(key, isolate(key, values) ?? values, formats);
  wrapped.raw = (key: string) => t.raw(key);
  wrapped.has = (key: string) => t.has(key);
  return wrapped as unknown as T;
}

export const useTranslations: typeof useIntlTranslations = (namespace) => {
  const translator = useIntlTranslations(namespace);
  return useMemo(() => isolating(translator), [translator]);
};
